"use client";

import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import {
  gleich,
  istGezogen,
  rechteckAusPunkten,
  tastenAktion,
  verschiebe,
  veraendereGroesse,
  type MarkierGriff,
  type MarkierRechteck,
} from "./markier-geometrie";

/**
 * Markierfläche (Zeichenebene des Markierwerkzeugs, V6 Z4; Design-Guide `markierflaeche.md`, Betreiber 01.11.2026 „Empfehlung, 1 nur
 * Zurück, 2 ja, 3 ja“): das Seitenbild mit Rechtecken, die man zieht, verschiebt und in der Größe ändert. Gleiche Bauart wie die SVG-Ebene der
 * `DokumentAnsicht` (Seitenbild und Formen in EINEM SVG, Lage und Größe als Attribute in Seiteneinheiten, nie `style=`, Farben nur
 * Theme-Tokens), aber bedienbar. Der Baustein zeigt und meldet; WAS gespeichert wird und ob die Person das darf, entscheidet der Aufrufer
 * (kontrolliert über `zonen`, `auswahlId`, `werkzeug`; jede Änderung geht als Ereignis hinaus, der Aufrufer ruft die Action).
 *
 * Art nie nur über Farbe (K3): Datenfeld = durchgezogener Rahmen, Eckmarke „D<n>“ im Kreis; Personenbereich = gestrichelter Rahmen,
 * Schraffur und Eckmarke „P<n>“ im Rechteck. Beide tragen die Art außerdem als Wort im `aria-label`.
 * Tastatur (K5): Tab springt zwischen den Rechtecken, Pfeile verschieben, Umschalt+Pfeile ändern die Größe, Entf löscht, Enter öffnet
 * die Zeile in der Liste, Esc wählt ab. Neue Rechtecke ohne Maus legt der Aufrufer über die Werkzeugleiste an.
 * Abweichung AW-152 (Sonderbau ohne Pro-Block, `docs/ui-preline/abweichungen.json`).
 * Maske: `maske` legt über Personenbereiche eine leicht markierte, halbtransparente Fläche mit dem Wort „Personenbereich“ (Entscheid 5);
 * ob und wann das Original gezeigt wird, entscheidet der Server, der Baustein stellt nur die Maske dar.
 */
export type MarkierArt = "datenfeld" | "personenbereich";
export type MarkierWerkzeug = "auswaehlen" | MarkierArt;
export type MarkierZoom = 100 | 125 | 150 | 200;

export interface MarkierZone extends MarkierRechteck {
  id: string;
  art: MarkierArt;
  /** Laufende Nummer in der Eckmarke (je Art). */
  nummer: number;
  /** Beschriftung für Hilfstechnik („Datenfeld 2: Auftragsnummer“); ohne Wort der Art. */
  label: string;
  /** Ein Vorschlag ist noch nicht übernommen: gestrichelt mit „?“-Rand statt voll gezeichnet. */
  vorschlag?: boolean;
}

/** Feste, vollständige Ketten je Zoomstufe: die Seite wächst in die Breite, die Fläche scrollt. */
const ZOOM_SEITE: Record<MarkierZoom, string> = {
  100: "relative mx-auto w-full",
  125: "relative mx-auto w-5/4",
  150: "relative mx-auto w-3/2",
  200: "relative mx-auto w-2/1",
};

const A4_HOEHE = 141.4;
const GRIFFE: readonly { griff: MarkierGriff; ax: 0 | 0.5 | 1; ay: 0 | 0.5 | 1 }[] = [
  { griff: "nw", ax: 0, ay: 0 },
  { griff: "n", ax: 0.5, ay: 0 },
  { griff: "ne", ax: 1, ay: 0 },
  { griff: "e", ax: 1, ay: 0.5 },
  { griff: "se", ax: 1, ay: 1 },
  { griff: "s", ax: 0.5, ay: 1 },
  { griff: "sw", ax: 0, ay: 1 },
  { griff: "w", ax: 0, ay: 0.5 },
];
/** Feste, vollständige Ketten je Griff (nie zusammengesetzt). */
const GRIFF_KLASSE: Record<MarkierGriff, string> = {
  nw: "fill-layer stroke-primary cursor-nwse-resize",
  se: "fill-layer stroke-primary cursor-nwse-resize",
  ne: "fill-layer stroke-primary cursor-nesw-resize",
  sw: "fill-layer stroke-primary cursor-nesw-resize",
  n: "fill-layer stroke-primary cursor-ns-resize",
  s: "fill-layer stroke-primary cursor-ns-resize",
  e: "fill-layer stroke-primary cursor-ew-resize",
  w: "fill-layer stroke-primary cursor-ew-resize",
};

const ART_WORT: Record<MarkierArt, string> = { datenfeld: "Datenfeld", personenbereich: "Personenbereich" };
const ART_KUERZEL: Record<MarkierArt, string> = { datenfeld: "D", personenbereich: "P" };

type Zug =
  | { art: "neu"; zeichen: MarkierArt; startX: number; startY: number; jetzt: MarkierRechteck }
  | { art: "bewegen"; id: string; startX: number; startY: number; original: MarkierRechteck; jetzt: MarkierRechteck }
  | { art: "groesse"; id: string; griff: MarkierGriff; startX: number; startY: number; original: MarkierRechteck; jetzt: MarkierRechteck };

export function Markierflaeche({
  id,
  bildUrl,
  hoehe = A4_HOEHE,
  zonen,
  auswahlId,
  beiAuswahl,
  werkzeug,
  beiNeu,
  beiAenderung,
  beiLoeschen,
  beiOeffnen,
  zoom = 100,
  maske = false,
  nurLesen = false,
  beiBildFehler,
  platzhalterSatz ="Für diese Seite gibt es kein Seitenbild. Die Rechtecke gelten für die Seite wie abgebildet.",
}: {
  id: string;
  /** Seitenbild (abgedeckte Fassung, oder Original nur auf dem geprüften Weg); `null` = Platzhalterseite. */
  bildUrl: string | null;
  /** Seitenhöhe in Einheiten bei Breite 100 (A4 hochkant als Vorgabe). */
  hoehe?: number;
  zonen: readonly MarkierZone[];
  auswahlId: string | null;
  beiAuswahl: (id: string | null) => void;
  werkzeug: MarkierWerkzeug;
  /** Ein Rechteck wurde mit dem Zeichenwerkzeug gezogen. */
  beiNeu: (art: MarkierArt, rechteck: MarkierRechteck) => void;
  /** Ein Rechteck wurde verschoben oder in der Größe geändert (einmal pro Geste, am Ende; per Tastatur je Tastendruck). */
  beiAenderung: (id: string, rechteck: MarkierRechteck) => void;
  beiLoeschen: (id: string) => void;
  /** Enter auf einem Rechteck: die zugehörige Zeile in der Liste öffnen. */
  beiOeffnen?: (id: string) => void;
  zoom?: MarkierZoom;
  maske?: boolean;
  /** Keine Änderung möglich (Person darf nur ansehen oder der Typ ist gesperrt). */
  nurLesen?: boolean;
  /** Das Seitenbild ließ sich nicht laden (Frist um, kein Recht, Fehler): der Aufrufer sagt es in einem Satz und gibt `bildUrl` ab. */
  beiBildFehler?: () => void;
  platzhalterSatz?: string;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [zug, setZug] = useState<Zug | null>(null);
  const schraffurId = `${id}-schraffur`;
  const zeichnet = !nurLesen && werkzeug !== "auswaehlen";

  const punkt = (e: { clientX: number; clientY: number }): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const fange = (e: ReactPointerEvent<SVGElement>) => {
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // Ohne Capture läuft die Geste weiter, solange der Zeiger über der Fläche bleibt.
    }
  };

  const beiFlaechenDruck = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.target !== e.currentTarget && !(e.target as Element).hasAttribute("data-seite")) return;
    if (!zeichnet) {
      beiAuswahl(null);
      return;
    }
    const p = punkt(e);
    if (!p) return;
    fange(e);
    setZug({ art: "neu", zeichen: werkzeug as MarkierArt, startX: p.x, startY: p.y, jetzt: rechteckAusPunkten(p.x, p.y, p.x, p.y) });
  };

  const beiZonenDruck = (e: ReactPointerEvent<SVGGElement>, z: MarkierZone) => {
    e.stopPropagation();
    beiAuswahl(z.id);
    if (nurLesen || werkzeug !== "auswaehlen") return;
    const p = punkt(e);
    if (!p) return;
    fange(e);
    const original = { x: z.x, y: z.y, b: z.b, h: z.h };
    setZug({ art: "bewegen", id: z.id, startX: p.x, startY: p.y, original, jetzt: original });
  };

  const beiGriffDruck = (e: ReactPointerEvent<SVGElement>, z: MarkierZone, griff: MarkierGriff) => {
    e.stopPropagation();
    if (nurLesen) return;
    const p = punkt(e);
    if (!p) return;
    fange(e);
    const original = { x: z.x, y: z.y, b: z.b, h: z.h };
    setZug({ art: "groesse", id: z.id, griff, startX: p.x, startY: p.y, original, jetzt: original });
  };

  const beiBewegung = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!zug) return;
    const p = punkt(e);
    if (!p) return;
    if (zug.art === "neu") setZug({ ...zug, jetzt: rechteckAusPunkten(zug.startX, zug.startY, p.x, p.y) });
    else if (zug.art === "bewegen") setZug({ ...zug, jetzt: verschiebe(zug.original, p.x - zug.startX, p.y - zug.startY) });
    else setZug({ ...zug, jetzt: veraendereGroesse(zug.original, zug.griff, p.x - zug.startX, p.y - zug.startY) });
  };

  const beiLoslassen = () => {
    if (!zug) return;
    const z = zug;
    setZug(null);
    if (z.art === "neu") {
      if (istGezogen(z.jetzt)) beiNeu(z.zeichen, z.jetzt);
    } else if (!gleich(z.jetzt, z.original)) {
      beiAenderung(z.id, z.jetzt);
    }
  };

  const beiTaste = (e: ReactKeyboardEvent<SVGGElement>, z: MarkierZone) => {
    if (e.target !== e.currentTarget) return;
    if (nurLesen && e.key !== "Enter" && e.key !== "Escape") return;
    const aktion = tastenAktion(e.key, e.shiftKey, { x: z.x, y: z.y, b: z.b, h: z.h });
    if (!aktion) return;
    e.preventDefault();
    if (aktion.art === "aendern") {
      if (!gleich(aktion.rechteck, { x: z.x, y: z.y, b: z.b, h: z.h })) beiAenderung(z.id, aktion.rechteck);
    } else if (aktion.art === "loeschen") beiLoeschen(z.id);
    else if (aktion.art === "oeffnen") beiOeffnen?.(z.id);
    else beiAuswahl(null);
  };

  /** Während einer Geste zeigt die Fläche das vorläufige Rechteck der gezogenen Zone. */
  const gezeigt = (z: MarkierZone): MarkierRechteck => (zug && zug.art !== "neu" && zug.id === z.id ? zug.jetzt : z);

  return (
    <>
      {/* Preline: ecommerce/ecommerce-sub-product-detail-purchase/ecommerce-cat-product-media-galleries/vertical-product-gallery-with-thumbnail-rail (Seitenfläche der DokumentAnsicht, M-150) */}
      <div className="rounded-lg border border-layer-line bg-surface p-3 overflow-auto max-h-192" data-markierflaeche={id} data-werkzeug={werkzeug}>
        <div className={ZOOM_SEITE[zoom]}>
          <svg
            ref={svgRef}
            className={zeichnet ? "block w-full h-auto rounded-sm shadow-xs cursor-crosshair touch-none" : "block w-full h-auto rounded-sm shadow-xs touch-none"}
            viewBox={`0 0 100 ${hoehe}`}
            preserveAspectRatio="none"
            role="group"
            aria-label="Seite mit Rechtecken"
            onPointerDown={beiFlaechenDruck}
            onPointerMove={beiBewegung}
            onPointerUp={beiLoslassen}
            onPointerCancel={() => setZug(null)}
          >
            <defs>
              <pattern id={schraffurId} width={2.4} height={2.4} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1={0} y1={0} x2={0} y2={2.4} strokeWidth={0.7} className="stroke-status-warn" />
              </pattern>
            </defs>
            {bildUrl ? (
              <image href={bildUrl} x={0} y={0} width={100} height={hoehe} preserveAspectRatio="none" aria-hidden="true" data-seite="" onError={beiBildFehler} />
            ) : (
              <g data-seite="">
                <rect x={0} y={0} width={100} height={hoehe} className="fill-layer" data-seite="" />
                <rect x={8} y={8} width={52} height={3} rx={0.6} className="fill-muted" data-seite="" />
                <rect x={8} y={14} width={34} height={2} rx={0.6} className="fill-muted" data-seite="" />
                <rect x={8} y={hoehe - 16} width={84} height={8} rx={0.6} className="fill-muted" data-seite="" />
              </g>
            )}

            {zonen.map((z) => {
              const r = gezeigt(z);
              const x = r.x * 100;
              const y = r.y * hoehe;
              const b = r.b * 100;
              const h = r.h * hoehe;
              const gewaehlt = z.id === auswahlId;
              const person = z.art === "personenbereich";
              const rahmen = person
                ? "fill-status-warn/10 stroke-status-warn"
                : gewaehlt
                  ? "fill-primary/20 stroke-primary"
                  : "fill-primary/10 stroke-primary";
              const strich = person ? "2.2 1.4" : z.vorschlag ? "1.2 1.2" : undefined;
              return (
                <g
                  key={z.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`${ART_WORT[z.art]} ${z.nummer}: ${z.label}${z.vorschlag ? " (Vorschlag)" : ""}`}
                  aria-pressed={gewaehlt}
                  data-zone={z.id}
                  data-art={z.art}
                  className={zeichnet ? "focus-visible:outline-2 focus-visible:outline-primary-focus" : "cursor-move focus-visible:outline-2 focus-visible:outline-primary-focus"}
                  onPointerDown={(e) => beiZonenDruck(e, z)}
                  onFocus={() => beiAuswahl(z.id)}
                  onKeyDown={(e) => beiTaste(e, z)}
                >
                  <rect x={x} y={y} width={b} height={h} rx={0.6} className={rahmen} strokeWidth={gewaehlt ? 1.1 : 0.7} strokeDasharray={strich} vectorEffect="non-scaling-stroke" />
                  {person && <rect x={x} y={y} width={b} height={h} rx={0.6} fill={`url(#${schraffurId})`} opacity={maske ? 0.55 : 0.3} pointerEvents="none" />}
                  {maske && person && (
                    <text x={x + b / 2} y={y + h / 2 + 1} textAnchor="middle" fontSize={3} fontWeight={600} className="fill-status-warn-text" pointerEvents="none">
                      Personenbereich
                    </text>
                  )}
                  {person ? (
                    <g aria-hidden="true" pointerEvents="none">
                      <rect x={x - 0.3} y={y - 3.4} width={6.4} height={3.4} rx={0.5} className="fill-status-warn" />
                      <text x={x + 2.9} y={y - 0.9} textAnchor="middle" fontSize={2.6} fontWeight={700} className="fill-primary-foreground">
                        {ART_KUERZEL[z.art]}
                        {z.nummer}
                      </text>
                    </g>
                  ) : (
                    <g aria-hidden="true" pointerEvents="none">
                      <circle cx={x + 1.9} cy={y - 1.9} r={2.1} className="fill-primary" />
                      <text x={x + 1.9} y={y - 0.9} textAnchor="middle" fontSize={2.4} fontWeight={700} className="fill-primary-foreground">
                        {ART_KUERZEL[z.art]}
                        {z.nummer}
                      </text>
                    </g>
                  )}
                  {gewaehlt &&
                    !nurLesen &&
                    GRIFFE.map((g) => (
                      <rect
                        key={g.griff}
                        x={x + b * g.ax - 1}
                        y={y + h * g.ay - 1}
                        width={2}
                        height={2}
                        rx={0.3}
                        strokeWidth={0.8}
                        vectorEffect="non-scaling-stroke"
                        className={GRIFF_KLASSE[g.griff]}
                        data-griff={g.griff}
                        onPointerDown={(e) => beiGriffDruck(e, z, g.griff)}
                      />
                    ))}
                </g>
              );
            })}

            {zug?.art === "neu" && (
              <rect
                x={zug.jetzt.x * 100}
                y={zug.jetzt.y * hoehe}
                width={zug.jetzt.b * 100}
                height={zug.jetzt.h * hoehe}
                rx={0.6}
                strokeWidth={0.9}
                strokeDasharray={zug.zeichen === "personenbereich" ? "2.2 1.4" : undefined}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
                className={zug.zeichen === "personenbereich" ? "fill-status-warn/15 stroke-status-warn" : "fill-primary/15 stroke-primary"}
                data-vorschau={zug.zeichen}
              />
            )}
          </svg>
        </div>
        {!bildUrl && <p className="mt-2 text-xs text-muted-foreground-1">{platzhalterSatz}</p>}
      </div>
    </>
  );
}
